import { useState } from 'react';

import { Alert, Button } from '@/components/ui';
import { useAnnouncer } from '@/lib/a11y';
import { fetchInvoice } from '@/lib/api/checkout';
import { toApiError, type ApiError } from '@/lib/api/client';

export interface DownloadInvoiceButtonProps {
  orderId: string;
  /** Names the saved file, so a folder of invoices is legible. */
  orderNo: string;
}

/**
 * "Download invoice" — a button, not a link, and that is the whole point.
 *
 * It used to be an `<a href={order.invoice_url}>`, which never worked: the
 * endpoint authenticates with the customer's access token, that token lives in
 * memory inside lib/api/client.ts, and a browser following a link sends cookies
 * and no Authorization header. Every customer who pressed it got a 401 envelope
 * where their invoice should have been.
 *
 * So the PDF is FETCHED, with the token attached like any other API call, and
 * handed to the browser as an object URL. The alternatives were worse: putting
 * a token in the query string writes a credential into history and server logs,
 * and widening the refresh cookie's path to cover /orders would attach a
 * long-lived credential to every order request for the sake of one button.
 *
 * The object URL is revoked once the click has been dispatched. Nothing else
 * holds a reference, and leaving it alive pins the whole PDF in memory for as
 * long as the document lives.
 */
export function DownloadInvoiceButton({ orderId, orderNo }: DownloadInvoiceButtonProps) {
  const { announce } = useAnnouncer();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const blob = await fetchInvoice(orderId);
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice-${orderNo}.pdf`;
        // Appended before clicking: Firefox ignores a click on an anchor that
        // is not in the document.
        document.body.append(link);
        link.click();
        link.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
      announce(`Invoice for ${orderNo} downloaded.`);
    } catch (thrown) {
      const apiError = toApiError(thrown);
      setError(apiError);
      announce(apiError.message, 'assertive');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error ? (
        <Alert severity="danger" title="We could not fetch your invoice" className="mb-3">
          <p>{error.message}</p>
        </Alert>
      ) : null}
      <Button
        variant="secondary"
        onClick={() => void download()}
        loading={busy}
        loadingText="Preparing your invoice…"
      >
        Download invoice
      </Button>
    </>
  );
}
